import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { Button, css, Flex, Form, Select, styled, Text } from '@goodrx/matisse';
import { trackLink, useDataAttributes } from '@goodrx/x-core';
import { Placeholder as PlaceholderType } from '@goodrx/x-core/dist/lib/types/Placeholder';
// @ts-ignore
import { DESKTOP_BREAKPOINTS, useBreakpoint } from '@goodrx/x-matisse/dist/cjs/utils/react/useBreakpoint';

const SelectWrapper = styled.div(
  (p) => css`
    label {
      transform: translateY(-0.05rem) !important;
    }

    @media (min-width: ${p.theme.breakpoints.md}) {
      max-width: 343px;
    }
  `
);

const StyledButton = styled(Button)(
  (p) => css`
    display: block;
    -webkit-text-decoration: none;
    text-decoration: none;
    min-height: 48px;
    width: 100%;

    @media (min-width: ${p.theme.breakpoints.md}) {
      max-width: 420px;
    }
  `
);

const DosageInfo = styled.div`
  margin-top: 0.5rem;
`;

const PriceInfo = styled(Flex)`
  margin-top: 2rem;
  margin-bottom: 1.5rem;
`;

const Pill = styled.div`
  display: inline-block;
  background-color: #00bc82;
  border-radius: 3px;
  padding: 0.3rem 0.75rem;
`;

const StyledSelect = styled(Select)`
  transform: translateY(0.5rem);
`;

const drugData = [
  {
    ndc: '00093551798',
    rxcui: '577033',
    displayName: 'Sildenafil',
    label: '20mg, 30 pills',
    dose: '20mg',
    quantity: 30,
    value: 'sku',
    cost: 810,
    msrp: 20642,
  },
  {
    ndc: '00093534156',
    rxcui: '314228',
    displayName: 'Sildenafil',
    label: '25mg, 30 pills',
    dose: '25mg',
    quantity: 30,
    value: 'sku-2',
    cost: 1180,
    msrp: 109184,
  },
  {
    ndc: '00093534201',
    rxcui: '312950',
    displayName: 'Sildenafil',
    label: '50mg, 30 pills',
    dose: '50mg',
    quantity: 30,
    value: 'sku-3',
    cost: 1293,
    msrp: 114309,
  },
  {
    ndc: '00093534201',
    rxcui: '312950',
    displayName: 'Sildenafil',
    label: '100mg, 30 pills',
    dose: '100mg',
    quantity: 30,
    value: 'sku-4',
    cost: 1543,
    msrp: 119054,
  },
];

export interface Props extends PlaceholderType {
  className?: string;
}

const formatPrice = (price: number): string => {
  return (price / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
};

export const DrugSelector = (props: Props) => {
  const { sys } = props;
  const router = useRouter();
  const { slug } = router.query;
  // Set default selection based on page route/slug
  const [selectedDrug, setSelectedDrug] = useState(
    (slug === 'sildenafil-25' && drugData[1]) ||
      (slug === 'sildenafil-50' && drugData[2]) ||
      (slug === 'sildenafil-100' && drugData[3]) ||
      drugData[0]
  );
  const selectRef = useRef<HTMLSelectElement>(null);
  const linkRef = useRef<HTMLAnchorElement>(null);

  const breakpoint = useBreakpoint();

  const displayTitle = 'Start Now';
  const id = sys.id;
  const title = 'Placeholder: HD: LP: DrugSelector: Sildenafil';
  const type = 'primary';
  const url = '/register';

  const attributes = useDataAttributes({
    content: displayTitle || '',
    contentId: id,
    contentTitle: title || '',
    screenSize: DESKTOP_BREAKPOINTS.includes(breakpoint) ? 'desktop' : 'mobile',
    type: type || 'default',
    url: url || '',
  });

  const handleSelectChange = useCallback(() => {
    const drug = drugData.find((item) => item.value === selectRef.current?.value);
    if (drug) {
      setSelectedDrug(drug);
    }
  }, [setSelectedDrug]);

  const handleButtonClick = useCallback(
    (evt: MouseEvent) => {
      evt.preventDefault();

      sessionStorage.setItem('hd.intendedServiceSlug', 'erectile-dysfunction');
      sessionStorage.setItem('hd.requestedDrug', JSON.stringify(selectedDrug));

      router.push('/register');
    },
    [router, selectedDrug]
  );

  useEffect(() => {
    if (!linkRef.current) {
      return;
    }

    trackLink(linkRef.current, `${displayTitle} CTA Clicked`, attributes);
  }, [attributes, displayTitle]);

  return (
    <Form style={{ width: '100%' }}>
      <SelectWrapper>
        <StyledSelect
          id="drug-select"
          floating
          label="Dosage and Quantity"
          name="drug-select"
          options={drugData}
          ref={selectRef}
          value={selectedDrug.value}
          onChange={handleSelectChange}
        />
        <DosageInfo>
          <Text color="greyDarkest" style={{ fontSize: '14px', lineHeight: 1.325 }}>
            Prescribed dosage will be based on your consultation with a healthcare provider.
          </Text>
        </DosageInfo>
      </SelectWrapper>
      <PriceInfo alignItems="center">
        <Text
          style={{
            fontSize: '22px',
            fontWeight: 800,
            margin: 0,
            marginRight: '1rem',
          }}>
          {formatPrice(selectedDrug.cost)}
        </Text>
        <Text color="greyDarker" style={{ fontSize: '18px', margin: 0, marginRight: '1rem' }}>
          <del>{formatPrice(selectedDrug.msrp)}</del> retail{' '}
        </Text>
        <Pill>
          <Text color="white" style={{ fontSize: '14px', fontWeight: 800, lineHeight: 1 }}>
            {`SAVE ${formatPrice(selectedDrug.msrp - selectedDrug.cost)}`}
            <sup>*</sup>
          </Text>
        </Pill>
      </PriceInfo>
      <StyledButton ref={linkRef} size="md" variant="primary" onClick={handleButtonClick}>
        Start Now
      </StyledButton>
    </Form>
  );
};
